import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight, Sparkles, Crown, Gem, ChevronDown, BadgeEuro, Banknote } from "lucide-react";
import { SERVICES } from "@/lib/site";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

function ServiceBlock({ service, index }) {
  return (
    <motion.article
      data-testid={`service-${service.id}`}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      className="relative"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* Bento images */}
        <div className="lg:col-span-7 grid grid-cols-6 grid-rows-2 gap-3 md:gap-4 h-[420px] md:h-[520px]">
          <div className="col-span-6 md:col-span-4 row-span-2 rounded-3xl overflow-hidden relative group shadow-lg">
            <img
              src={service.images[0]}
              alt={`${service.title} – Beispielbild 1`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="col-span-3 md:col-span-2 rounded-3xl overflow-hidden relative group shadow-md">
            <img
              src={service.images[1]}
              alt={`${service.title} – Beispielbild 2`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          </div>
          <div className="col-span-3 md:col-span-2 rounded-3xl overflow-hidden relative group shadow-md">
            <img
              src={service.images[2]}
              alt={`${service.title} – Beispielbild 3`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          </div>
        </div>

        {/* Text */}
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 text-green-accent text-sm font-bold uppercase tracking-[0.18em]" style={{ color: "#3d8a2c" }}>
            <span className="w-8 h-[2px] rounded-full" style={{ backgroundColor: "#4ea83a" }} />
            0{index + 1} · Leistung
          </div>
          <h3 className="font-heading mt-4 text-3xl md:text-4xl lg:text-5xl font-extrabold text-navy tracking-tight">
            {service.title}
          </h3>
          <p className="mt-3 text-base md:text-lg font-semibold text-slate-700">
            {service.kicker}
          </p>
          <div className="mt-5 space-y-4 text-slate-600 leading-relaxed text-base md:text-lg">
            {(service.paragraphs || [service.text]).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {service.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-800">
                <span
                  className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(78,168,58,0.15)" }}
                >
                  <Check className="w-4 h-4" style={{ color: "#3d8a2c" }} />
                </span>
                <span className="text-sm md:text-base font-medium">{b}</span>
              </li>
            ))}
          </ul>

          <a
            href="#kontakt"
            data-testid={`service-cta-${service.id}`}
            className="group mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white font-semibold hover:bg-navy-deep transition-colors"
            style={{ backgroundColor: "#1e3a5f" }}
          >
            Jetzt Angebot anfragen
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </motion.article>
  );
}

/* PREMIUM Gold Variant — completely distinct styling */
function GoldServiceBlock({ service, index }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <motion.article
      id="service-goldankauf"
      data-testid={`service-${service.id}`}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.15 }}
      className="relative scroll-mt-24"
    >
      {/* Premium dark container with gold sheen */}
      <div
        className="relative rounded-[2.5rem] overflow-hidden p-6 md:p-10 lg:p-14"
        style={{
          background:
            "linear-gradient(135deg, #0f1d33 0%, #1e3a5f 45%, #2a1a0a 100%)",
        }}
      >
        {/* Decorative gold glow */}
        <div
          className="absolute -top-40 -right-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "radial-gradient(circle, #d4a017 0%, transparent 65%)" }}
        />
        <div
          className="absolute -bottom-40 -left-32 w-[480px] h-[480px] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{ background: "radial-gradient(circle, #f1c40f 0%, transparent 70%)" }}
        />
        {/* Gold corner ribbon */}
        <div className="absolute top-6 right-6 z-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.22em] shadow-lg"
            style={{
              background: "linear-gradient(135deg, #fde68a 0%, #d4a017 50%, #92590a 100%)",
              color: "#1a0f00",
            }}
          >
            <Crown className="w-3.5 h-3.5" />
            Premium · Edel
          </div>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Image left on gold variant (consistent layout) */}
          <div className="lg:col-span-6 lg:order-1 order-2 grid grid-cols-6 grid-rows-2 gap-3 md:gap-4 h-[420px] md:h-[540px]">
            <div className="col-span-6 md:col-span-4 row-span-2 rounded-3xl overflow-hidden relative group shadow-2xl ring-1" style={{ borderColor: "rgba(244,196,48,0.3)" }}>
              <img
                src={service.images[0]}
                alt={`${service.title} – Bild 1`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div
                className="absolute inset-0 opacity-50 mix-blend-overlay pointer-events-none"
                style={{ background: "radial-gradient(circle at 30% 20%, rgba(244,196,48,0.3), transparent 60%)" }}
              />
              <div className="absolute inset-0 rounded-3xl ring-2 ring-inset" style={{ boxShadow: "inset 0 0 60px rgba(244,196,48,0.15)" }} />
            </div>
            <div className="col-span-3 md:col-span-2 rounded-3xl overflow-hidden relative group shadow-xl">
              <img
                src={service.images[1]}
                alt={`${service.title} – Bild 2`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(20,15,0,0.5))" }} />
            </div>
            <div className="col-span-3 md:col-span-2 rounded-3xl overflow-hidden relative group shadow-xl">
              <img
                src={service.images[2]}
                alt={`${service.title} – Bild 3`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(20,15,0,0.5))" }} />
            </div>
          </div>

          {/* Text right on gold variant */}
          <div className="lg:col-span-6 lg:order-2 order-1 text-white">
            <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em]" style={{ color: "#f4c430" }}>
              <span className="w-8 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #f4c430, #d4a017)" }} />
              0{index + 1} · Premium Leistung
            </div>

            <h3
              className="font-heading mt-4 text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]"
              style={{
                background: "linear-gradient(135deg, #fff8dc 0%, #f4c430 35%, #d4a017 70%, #fff8dc 100%)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {service.title}
            </h3>

            <p className="mt-4 text-lg md:text-xl font-semibold" style={{ color: "#fde68a" }}>
              {service.kicker}
            </p>
            <div className="mt-5 space-y-4 text-white/80 leading-relaxed text-base md:text-lg max-w-xl">
              {(service.paragraphs || [service.text]).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {/* Premium feature pills */}
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {service.bullets.map((b, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-2xl"
                  style={{
                    background: "rgba(244,196,48,0.08)",
                    border: "1px solid rgba(244,196,48,0.2)",
                  }}
                >
                  <span
                    className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, #f4c430, #d4a017)" }}
                  >
                    <Check className="w-3.5 h-3.5" style={{ color: "#1a0f00" }} strokeWidth={3} />
                  </span>
                  <span className="text-sm md:text-base font-medium text-white">{b}</span>
                </div>
              ))}
            </div>

            {/* Price/value strip */}
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-white/90">
                <Sparkles className="w-4 h-4" style={{ color: "#f4c430" }} />
                <span className="text-sm font-semibold">Tagesaktueller Bestpreis</span>
              </div>
              <div className="w-px h-5 bg-white/20" />
              <div className="flex items-center gap-2 text-white/90">
                <Gem className="w-4 h-4" style={{ color: "#f4c430" }} />
                <span className="text-sm font-semibold">Faire Bewertung vor Ort</span>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#kontakt"
                data-testid={`service-cta-${service.id}`}
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold shadow-2xl hover:-translate-y-0.5 transition-all"
                style={{
                  background: "linear-gradient(135deg, #fde68a 0%, #f4c430 30%, #d4a017 70%, #92590a 100%)",
                  color: "#1a0f00",
                  boxShadow: "0 10px 40px rgba(212,160,23,0.4)",
                }}
              >
                Hausbesuch vereinbaren
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <button
                type="button"
                onClick={() => setExpanded((s) => !s)}
                data-testid="gold-learn-more-toggle"
                aria-expanded={expanded}
                className="inline-flex items-center gap-2 px-6 py-4 rounded-full font-semibold text-white border transition-colors hover:bg-white/10"
                style={{ borderColor: "rgba(244,196,48,0.4)" }}
              >
                {expanded ? "Weniger anzeigen" : "Mehr erfahren"}
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom gold strip */}
        <div className="relative mt-10 pt-6 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "rgba(244,196,48,0.18)" }}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-white/90 text-sm font-medium">
            {["Gold", "Schmuck", "Silber", "Münzen", "Zahngold", "Edelmetalle"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#f4c430" }} />
                {t}
              </span>
            ))}
          </div>
          <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "#f4c430" }}>
            Diskret · Seriös · Sicher
          </div>
        </div>

        {/* Expandable process panel */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="gold-process"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden"
              data-testid="gold-process-panel"
            >
              <div className="pt-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div
                    className="p-5 rounded-2xl flex items-start gap-4"
                    style={{ background: "rgba(244,196,48,0.08)", border: "1px solid rgba(244,196,48,0.25)" }}
                  >
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "linear-gradient(135deg, #f4c430, #d4a017)" }}
                    >
                      <Banknote className="w-6 h-6" style={{ color: "#1a0f00" }} />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "#f4c430" }}>
                        Bis 2.000 €
                      </div>
                      <div className="font-heading text-xl font-bold text-white mt-0.5">
                        Sofortige Barauszahlung
                      </div>
                      <div className="text-sm text-white/75 mt-1">
                        Beträge bis 2.000 € erhalten Sie direkt in bar — unkompliziert und sofort.
                      </div>
                    </div>
                  </div>
                  <div
                    className="p-5 rounded-2xl flex items-start gap-4"
                    style={{ background: "rgba(244,196,48,0.08)", border: "1px solid rgba(244,196,48,0.25)" }}
                  >
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "linear-gradient(135deg, #f4c430, #d4a017)" }}
                    >
                      <BadgeEuro className="w-6 h-6" style={{ color: "#1a0f00" }} />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "#f4c430" }}>
                        Über 2.000 €
                      </div>
                      <div className="font-heading text-xl font-bold text-white mt-0.5">
                        Direktüberweisung
                      </div>
                      <div className="text-sm text-white/75 mt-1">
                        Höhere Beträge zahlen wir sicher per Direktüberweisung – noch am gleichen Tag auf Ihrem Konto.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div
                    className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em]"
                    style={{ color: "#f4c430" }}
                  >
                    <span className="w-8 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #f4c430, #d4a017)" }} />
                    So läuft der Ankauf ab
                  </div>
                  <h4 className="font-heading mt-3 text-2xl md:text-3xl font-bold text-white">
                    Transparent und vor Ihren Augen – Schritt für Schritt.
                  </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(service.process || []).map((step) => (
                    <div
                      key={step.step}
                      className="relative p-5 rounded-2xl"
                      style={{
                        background: "rgba(255,255,255,0.04)",
                        border: "1px solid rgba(244,196,48,0.2)",
                      }}
                    >
                      <div
                        className="absolute -top-3 -left-3 w-10 h-10 rounded-full flex items-center justify-center font-heading font-black text-base shadow-lg"
                        style={{ background: "linear-gradient(135deg, #fde68a, #d4a017)", color: "#1a0f00" }}
                      >
                        {step.step}
                      </div>
                      <h5 className="font-heading text-lg font-bold text-white mt-2">
                        {step.title}
                      </h5>
                      <p className="mt-2 text-sm text-white/75 leading-relaxed">
                        {step.text}
                      </p>
                    </div>
                  ))}
                </div>

                <div
                  className="mt-8 p-5 rounded-2xl text-center text-sm md:text-base"
                  style={{
                    background: "linear-gradient(135deg, rgba(244,196,48,0.18), rgba(212,160,23,0.08))",
                    border: "1px solid rgba(244,196,48,0.3)",
                    color: "#fff8dc",
                  }}
                >
                  <strong style={{ color: "#fde68a" }}>100 % Sicherheit für Sie:</strong>{" "}
                  Sie behalten Ihre Stücke jederzeit im Blick, wir wiegen und prüfen vor Ihren Augen,
                  Sie erhalten einen schriftlichen Ankaufsbeleg – ohne Verkaufsdruck.
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section id="leistungen" className="relative bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-navy text-xs font-bold uppercase tracking-[0.2em]">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#4ea83a" }} />
            Unsere Leistungen
          </div>
          <h2 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black text-navy tracking-tight leading-[1.05]">
            Alles aus einer Hand —{" "}
            <span className="text-shine">zuverlässig & fair.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Von der ersten Besichtigung bis zur besenreinen Übergabe übernehmen wir alle Aufgaben rund
            um Haushaltsauflösung, Entrümpelung und Ankauf. Persönlich, transparent und mit Festpreis.
          </p>
        </motion.div>

        <div className="mt-20 space-y-28 md:space-y-36">
          {SERVICES.map((s, i) =>
            s.id === "goldankauf" ? (
              <GoldServiceBlock key={s.id} service={s} index={i} />
            ) : (
              <ServiceBlock key={s.id} service={s} index={i} />
            )
          )}
        </div>
      </div>
    </section>
  );
}
